import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { addToast, handleAxiosError } from 'core/notifications';
import i18n from 'i18next';
import isEqual from 'lodash/isEqual';
import { withContainer } from 'core/cache';

import axios from '@/util/axios';

import useConfirmAction from '../hooks/useConfirmAction';
import useModalsShim from '../hooks/useModalsShim';

import NewItemForm from '../components/newItemForm';

const NewItemFormContainer = ({
  cancelText = '',
  confirmBody = '',
  confirmTitle = i18n.t('confirm-action'),
  getAxiosConfig = () => {},
  getIsConfirmationRequired = null,
  getSchema = null,
  id,
  isFullScreen = false,
  isFullWidth = false,
  isSubmitDisabledIfModelUnchanged = false,
  onDataErrors = null,
  onSubmit = null,
  schema = {},
  schemas = [],
  submitColor = 'brand',
  submitText = '',
}) => {
  const { getModel, setModel, removeModal } = useModalsShim(id);
  const [model, setDataModel] = useState(getModel(id));
  const [initialModel] = useState(model);
  const [formState, setFormState] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(isSubmitDisabledIfModelUnchanged);
  const getConfirmation = useConfirmAction({ body: confirmBody, title: confirmTitle });

  const forceUpdate = () => {
    setDataModel({ ...model });
  };

  const getSchemas = () => {
    if (schemas.length) return schemas;

    return [{ schema: getSchema?.(model) || schema }];
  };

  const handleCancel = () => {
    removeModal();
  };

  const handleDataErrors = ({ response }) => {
    const errors = response.data?.errors;

    if (onDataErrors) return onDataErrors({ errors, forceUpdate, formState });

    errors?.forEach(({ message, source }) => {
      const field = formState[source.split('data.').pop()];

      if (field) {
        Object.assign(field, { error: message, hasError: true });
      }
    });

    forceUpdate();
    addToast(i18n.t('form-errors'), 'warning');
  };

  const handleLoad = (state) => {
    setFormState((prevFormState) => ({ ...prevFormState, ...state.formState }));
  };

  const handleSubmit = async () => {
    if (getIsConfirmationRequired?.(model) && !(await getConfirmation())) return;

    setIsSubmitting(true);

    try {
      if (onSubmit) {
        return await onSubmit(model, removeModal);
      }

      removeModal({
        payload: (await axios(getAxiosConfig(model, formState))).data?.data,
        type: 'submit',
      });
    } catch (error) {
      setIsSubmitting(false);
      (error.response?.status === 422 ? handleDataErrors : handleAxiosError)(error);
    }
  };

  const handleUpdate = (state) => {
    const data = { ...model, ...state.update };

    setFormState({ ...formState, ...state.formState });
    setIsSubmitDisabled(isSubmitDisabledIfModelUnchanged && isEqual(data, initialModel));
    setModel(id, data);
    setDataModel(data);
  };

  return (
    <NewItemForm
      cancelText={cancelText}
      hasError={Object.values(formState).some(({ hasError }) => hasError)}
      isFullScreen={isFullScreen}
      isFullWidth={isFullWidth}
      isSubmitDisabled={isSubmitDisabled}
      isSubmitting={isSubmitting}
      model={model}
      onCancel={handleCancel}
      onLoad={handleLoad}
      onSubmit={handleSubmit}
      onUpdate={handleUpdate}
      schemas={getSchemas()}
      submitColor={submitColor}
      submitText={submitText}
    />
  );
};

NewItemFormContainer.propTypes = {
  cancelText: PropTypes.string,
  confirmBody: PropTypes.string,
  confirmTitle: PropTypes.string,
  getAxiosConfig: PropTypes.func,
  getIsConfirmationRequired: PropTypes.func,
  getSchema: PropTypes.func,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isFullScreen: PropTypes.bool,
  isFullWidth: PropTypes.bool,
  isSubmitDisabledIfModelUnchanged: PropTypes.bool,
  onDataErrors: PropTypes.func,
  onSubmit: PropTypes.func,
  schema: PropTypes.shape({}),
  schemas: PropTypes.arrayOf(
    PropTypes.shape({ schema: PropTypes.shape({}), title: PropTypes.string }),
  ),
  submitColor: PropTypes.oneOf(['primary', 'secondary', 'brand', 'negative']),
  submitText: PropTypes.string,
};

export default withContainer(NewItemFormContainer, {
  cache: {
    notifications: { isListener: true },
    notificationsModalsById: { isListener: true },
  },
});
